import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import '../../scss/styles.scss';
import { GreenroomState as GS } from '../../stores/types/types';
import { handleClickJacking } from '../../utils/utils';
import useRoomHealth, { overallStatus } from '../../hooks/health/use-room-health';
import useResolvedRoom, { ResolveState } from '../../hooks/health/use-resolved-room';
import useClientHealthReports from '../../hooks/health/use-client-health-reports';
import ClientQualityPanel from './panels/client-quality-panel';
import {
	DEFAULT_POLL_INTERVAL_MS,
	POLL_INTERVALS_MS,
	ProbeStatus,
} from './types';
import StatusPill from './status-pill';
import OpentokPanel from './panels/opentok-panel';
import BroadcastPanel from './panels/broadcast-panel';
import RoomSessionPanel from './panels/room-session-panel';
import ConnectedClientsPanel from './panels/connected-clients-panel';
import { formatAgo } from './format';

const intervalLabel = (ms: number) => (ms ? `${ms / 1000}s` : 'paused');

/**
 * Internal, read-only view of one meeting's services. Rendered outside the
 * meeting app on `/health/:meetingSlug`, so it never touches the OpenTok or
 * LiveKit clients and cannot disturb a live show. Everything it shows comes
 * from server side state, which also means it is safe to open on a room this
 * producer is not joined to.
 *
 * The url takes the same slug producers already have in `/producer/<slug>`, so
 * a producer can swap the path segment on a room they are working in.
 */
export default function HealthDashboard() {
	const { meetingSlug } = useParams<{ meetingSlug: string }>();
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const host = useSelector((s: GS) => s.SessionReducer.host);

	const [intervalMs, setIntervalMs] = useState<number>(DEFAULT_POLL_INTERVAL_MS);
	// Drives the "x ago" label between polls without re-running any probes.
	const [, setTick] = useState(0);

	// Only producers get to resolve or probe anything: hooks run before the
	// guards below can return, so the check has to gate their inputs too.
	const resolved = useResolvedRoom(host ? meetingSlug : undefined, token ?? null);
	const roomId = resolved.meeting?.roomId ?? null;

	const { health, isRefreshing, refresh } = useRoomHealth({
		roomId,
		token: token ?? null,
		intervalMs,
	});

	// Client-reported OpenTok quality. Read-only join of the meeting channel: the
	// dashboard never registers as a connected user, it only asks and listens.
	const {
		reports,
		connected: socketConnected,
		requestNow,
	} = useClientHealthReports(roomId, intervalMs);

	// index.html ships `html { display: none }` and every entry point has to earn
	// its way out of that via the clickjacking guard. Standalone routes are not
	// covered by App's call, so this route makes its own - it both reveals the
	// page and keeps the frame-busting protection.
	useEffect(() => {
		handleClickJacking();
	}, []);

	useEffect(() => {
		const timer = setInterval(() => setTick((count) => count + 1), 1000);
		return () => clearInterval(timer);
	}, []);

	useEffect(() => {
		document.title = meetingSlug ? `Health · ${meetingSlug}` : 'Health';
	}, [meetingSlug]);

	if (!token) {
		return (
			<div className='health-dashboard health-dashboard--message'>
				<h1>Meeting health</h1>
				<p>Signing in…</p>
			</div>
		);
	}

	if (!host) {
		return (
			<div className='health-dashboard health-dashboard--message'>
				<h1>Meeting health</h1>
				<p>This view is for producers. Sign in with a producer account to see this room.</p>
			</div>
		);
	}

	if (resolved.state === ResolveState.Pending) {
		return (
			<div className='health-dashboard health-dashboard--message'>
				<h1>Meeting health</h1>
				<p>Looking up <span className='health-mono'>{meetingSlug}</span>…</p>
			</div>
		);
	}

	if (resolved.state !== ResolveState.Resolved || !roomId) {
		return (
			<div className='health-dashboard health-dashboard--message'>
				<h1>Meeting health</h1>
				<p>
					{resolved.state === ResolveState.Forbidden
						&& 'You do not have access to this room.'}
					{resolved.state === ResolveState.NotFound
						&& 'No room matches this url.'}
					{resolved.state === ResolveState.Failed
						&& `Could not look up this room: ${resolved.error}`}
				</p>
				<p className='health-subtle'>
					Use the slug from the producer url, for example
					<span className='health-mono'> /health/{meetingSlug || 'abc123'}</span>.
				</p>
			</div>
		);
	}

	const overall = overallStatus(health);

	return (
		<div className='health-dashboard'>
			<header className='health-dashboard__header'>
				<div className='health-dashboard__identity'>
					<h1>
						{resolved.meeting?.standaloneTitle || resolved.meeting?.slug}
						<StatusPill status={overall} large />
					</h1>
					<p className='health-subtle'>
						<span className='health-mono'>{resolved.meeting?.slug}</span>
						{' · room '}
						<span className='health-mono'>{roomId}</span>
						{' · '}
						{health
							? `updated ${formatAgo(health.fetchedAt)}`
							: 'waiting for first response'}
						{isRefreshing && ' · refreshing'}
					</p>
				</div>

				<div className='health-dashboard__controls'>
					<label htmlFor='health-interval'>Refresh</label>
					<select
						id='health-interval'
						value={intervalMs}
						onChange={(event) => setIntervalMs(Number(event.target.value))}
					>
						{POLL_INTERVALS_MS.map((ms) => (
							<option key={ms} value={ms}>{intervalLabel(ms)}</option>
						))}
					</select>
					<button
						type='button'
						onClick={() => { refresh(); requestNow(); }}
						disabled={isRefreshing}
					>
						Refresh now
					</button>
				</div>
			</header>

			{overall === ProbeStatus.Error && (
				<p className='health-banner health-banner--error'>
					One or more services did not answer. Check the panel headers for the failing probe.
				</p>
			)}

			<div className='health-dashboard__grid'>
				<RoomSessionPanel probe={health?.roomSession} health={health} />
				<OpentokPanel
					probe={health?.opentok}
					users={health?.connectedUsers.data ?? []}
				/>
				<BroadcastPanel probe={health?.broadcast} />
				<ConnectedClientsPanel probe={health?.connectedUsers} />
			</div>

			<ClientQualityPanel
				reports={reports}
				connected={socketConnected}
				onRefresh={requestNow}
			/>

			<footer className='health-dashboard__footer health-subtle'>
				Read-only. Sources: sync room entities, spielberg (LiveKit egress), greenroom-socket,
				and OpenTok stats reported by the participants themselves.
			</footer>
		</div>
	);
}
